import { Accessor, createMemo, createSignal, Setter } from 'solid-js';

/**
 * Handles and provides data loaded to viewer(s). Consumed by capabilities and
 * also UI components which handles readonly data.
 */
export abstract class CapabilityProviderBase<
  TItem,
  TKey extends keyof TItem = keyof TItem,
> {
  public abstract idKey: TKey;

  public abstract cleanup(): Promise<void>;

  // Generic loading state tracking
  protected readonly loadingItems: Accessor<Set<string>>;
  private readonly setLoadingItems: Setter<Set<string>>;

  public readonly isLoading: Accessor<boolean>;

  public isItemLoading(itemId: TItem[TKey]): boolean {
    return this.loadingItems().has(String(itemId));
  }

  protected addToLoadingSet(itemId: TItem[TKey]): void {
    const currentSet = this.loadingItems();
    const newSet = new Set(currentSet);
    newSet.add(String(itemId));
    this.setLoadingItems(newSet);
  }

  protected removeFromLoadingSet(itemId: TItem[TKey]): void {
    const currentSet = this.loadingItems();
    const newSet = new Set(currentSet);
    newSet.delete(String(itemId));
    this.setLoadingItems(newSet);
  }

  public getById(id: TItem[TKey]): TItem | undefined {
    const items = this.items();
    const res = items.find((m) => {
      // This will work regardless of whether the property is a direct property or a getter
      const itemId = m[this.idKey];
      return itemId === id;
    });

    return res;
  }

  public readonly items: Accessor<TItem[]>;
  protected readonly setItems: Setter<TItem[]>;

  public readonly selectedItemId: Accessor<TItem[TKey] | null>;
  public readonly setSelectedItemId: Setter<TItem[TKey] | null>;
  public readonly selectedItem: Accessor<TItem | null>;

  public readonly hasItems: Accessor<boolean>;

  constructor() {
    const [items, setItems] = createSignal<TItem[]>([]);
    this.items = items;
    this.setItems = setItems;

    const [loadingItems, setLoadingItems] = createSignal<Set<string>>(
      new Set()
    );
    this.loadingItems = loadingItems;
    this.setLoadingItems = setLoadingItems;

    this.isLoading = createMemo(() => this.loadingItems().size > 0);

    const [selectedItemId, setSelectedItemId] = createSignal<
      TItem[TKey] | null
    >(null);
    this.selectedItemId = selectedItemId;
    this.setSelectedItemId = setSelectedItemId;

    this.selectedItem = createMemo(() => {
      const items = this.items();
      return items.find((m) => m[this.idKey] == this.selectedItemId()) ?? null;
    });

    this.hasItems = createMemo(() => {
      return this.items().length > 0;
    });
  }

  protected addItem(item: TItem): boolean {
    const items = this.items();
    if (items.some((m) => m === item)) {
      return false;
    }
    this.setItems([item, ...items]);
    return true;
  }

  protected removeItem(item: TItem): boolean {
    const items = this.items();
    if (!items.some((m) => m === item)) {
      return false;
    }
    this.setItems(items.filter((m) => m !== item));
    return true;
  }

  public isItemLoaded(itemId: TItem[TKey]): boolean {
    return this.getById(itemId) != undefined;
  }

  public isItemSelected(itemId: TItem[TKey]): boolean {
    return this.selectedItemId() == itemId;
  }
}
